import { Flex, Text } from "@jsq/ds/core";
import { Icon, IconType } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import { ReviewerConclusion } from "../../CaseDetailsDrawer/types";
import { INVALID_RESOLUTION_VALUES } from "../../constants";
import { resolutionContainer } from "./styles_vanilla.css";
import type { CaseResolution } from "../../types";

type ResolutionConfig = {
  displayText: string;
  iconType?: IconType;
  iconColor?: Colors;
};

const INVALID_RESOLUTION_DISPLAY_CONFIG: ResolutionConfig = {
  displayText: "Invalid",
  iconType: "xmark-circle",
  iconColor: Colors.Danger600,
};

const BILLING_RESOLUTION_CONFIG: Partial<Record<string, ResolutionConfig>> = {
  APPROVED: {
    displayText: "Approved",
    iconType: "circle-check",
    iconColor: Colors.Success600,
  },
  // FA cases resolve as INVALID, GP EUM cases as REJECTED; both render as "Invalid".
  ...Object.fromEntries(
    INVALID_RESOLUTION_VALUES.map((resolution) => [
      resolution,
      INVALID_RESOLUTION_DISPLAY_CONFIG,
    ]),
  ),
};

const REVIEWER_CONCLUSION_CONFIG: Partial<Record<string, ResolutionConfig>> = {
  [ReviewerConclusion.Approved]: {
    displayText: "Approved",
    iconType: "check",
    iconColor: Colors.Success600,
  },
  [ReviewerConclusion.Invalid]: {
    displayText: "Invalid",
    iconType: "xmark-circle",
    iconColor: Colors.Neutral900,
  },
};

const DEFAULT_CONFIG: ResolutionConfig = { displayText: "--" };

type ResolutionRendererProps = {
  resolution: CaseResolution | ReviewerConclusion | null;
  isReviewerConclusion?: boolean;
};

export const ResolutionRenderer = ({
  resolution,
  isReviewerConclusion = false,
}: ResolutionRendererProps) => {
  if (!resolution) {
    return (
      <Flex alignItems="center" gap={Sizes.SM} className={resolutionContainer}>
        <Text>{DEFAULT_CONFIG.displayText}</Text>
      </Flex>
    );
  }

  const config = isReviewerConclusion
    ? REVIEWER_CONCLUSION_CONFIG[resolution]
    : BILLING_RESOLUTION_CONFIG[resolution];

  const { displayText, iconType, iconColor } = config ?? DEFAULT_CONFIG;

  return (
    <Flex alignItems="center" gap={Sizes.SM} className={resolutionContainer}>
      {iconType && (
        <Icon
          type={iconType}
          color={iconColor}
          aria-label="resolution icon"
          aria-hidden={false}
        />
      )}
      <Text color={iconColor}>{displayText}</Text>
    </Flex>
  );
};
